import { useState, type KeyboardEvent } from "react";
import { Loader2, Star } from "lucide-react";
import { Button } from "@baluu/ui/components/button";
import { Checkbox } from "@baluu/ui/components/checkbox";
import { Input } from "@baluu/ui/components/input";
import { Label } from "@baluu/ui/components/label";
import { Textarea } from "@baluu/ui/components/textarea";
import { cn } from "@baluu/ui/lib/utils";
import { clickStar, emptyReview, ratingForKey, validateReview, type ReviewFormErrors, type ReviewFormValues } from "./reviewForm";

// The review form: the name shown with the review (first name, optional
// last name, "only show first name"), the stars and the words. Port of v1's
// ReviewForm + RatingInput, with the same fields, copy and checks; errors
// show once the customer tries to send.

export function ReviewForm({
  defaultName,
  submitting,
  onSubmit,
}: {
  defaultName: string;
  submitting: boolean;
  onSubmit: (values: ReviewFormValues) => void;
}) {
  const [values, setValues] = useState(() => emptyReview(defaultName));
  const [errors, setErrors] = useState<ReviewFormErrors>({});
  const [tried, setTried] = useState(false);

  function set<K extends keyof ReviewFormValues>(key: K, value: ReviewFormValues[K]) {
    const next = { ...values, [key]: value };
    setValues(next);
    if (tried) setErrors(validateReview(next));
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setTried(true);
    const found = validateReview(values);
    setErrors(found);
    if (Object.keys(found).length === 0) onSubmit(values);
  }

  return (
    <form onSubmit={submit} noValidate className="space-y-5">
      <p className="text-sm text-muted-foreground">
        Please confirm the name you would like shown alongside this review. To protect your identity online, you only need include your first
        or nickname.
      </p>
      <Field id="review-first-name" label="First name*" error={errors.firstName}>
        <Input id="review-first-name" value={values.firstName} onChange={(e) => set("firstName", e.target.value)} aria-invalid={!!errors.firstName} />
      </Field>
      <Field id="review-last-name" label="Last name">
        <Input id="review-last-name" value={values.lastName} onChange={(e) => set("lastName", e.target.value)} />
      </Field>
      <div className="flex items-center gap-2">
        <Checkbox
          id="review-first-name-only"
          checked={values.showOnlyFirstName}
          onCheckedChange={(c) => set("showOnlyFirstName", c === true)}
        />
        <Label htmlFor="review-first-name-only">Only show first name</Label>
      </div>
      <div className="pt-2 text-center">
        <RatingInput value={values.rating} onChange={(v) => set("rating", v)} />
        {errors.rating && <p className="mt-1 text-sm text-destructive">{errors.rating}</p>}
      </div>
      <div>
        <Textarea
          placeholder="Let us know your opinion"
          rows={5}
          aria-label="Your opinion"
          value={values.opinion}
          onChange={(e) => set("opinion", e.target.value)}
          aria-invalid={!!errors.opinion}
        />
        {errors.opinion && <p className="mt-1 text-sm text-destructive">{errors.opinion}</p>}
      </div>
      <div className="flex justify-center">
        <Button type="submit" disabled={submitting} className="w-full max-w-[270px]">
          {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {submitting ? "Submitting…" : "Leave review"}
        </Button>
      </div>
    </form>
  );
}

function Field({ id, label, error, children }: { id: string; label: string; error?: string; children: React.ReactNode }) {
  return (
    <div>
      <Label htmlFor={id} className="mb-2 block">
        {label}
      </Label>
      {children}
      {error && <p className="mt-1 text-sm text-destructive">{error}</p>}
    </div>
  );
}

/** 0–5 stars: click to pick (the current star again clears), hover to
 * preview, arrows or a digit from the keyboard. */
export function RatingInput({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [hovered, setHovered] = useState<number | null>(null);
  const shown = hovered ?? value;

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const next = ratingForKey(e.key, value);
    if (next === null) return;
    e.preventDefault();
    if (next !== value) onChange(next);
  }

  return (
    <div
      className="inline-flex gap-1 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      tabIndex={0}
      role="slider"
      aria-label="Rating"
      aria-valuemin={0}
      aria-valuemax={5}
      aria-valuenow={value}
      aria-valuetext={`${value} of 5 stars`}
      onKeyDown={onKeyDown}
      onMouseLeave={() => setHovered(null)}
    >
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          tabIndex={-1}
          aria-label={`${star} star${star > 1 ? "s" : ""}`}
          className="cursor-pointer bg-transparent p-0 leading-none"
          onClick={() => onChange(clickStar(star, value))}
          onMouseEnter={() => setHovered(star)}
        >
          <Star className={cn("size-8 transition-colors", star <= shown ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40")} />
        </button>
      ))}
    </div>
  );
}
